import { useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { useSiteContent, FONT_OPTIONS } from "@/context/SiteContentContext";
import ImageUploader from "@/components/ImageUploader";
import ProductToneSection from "@/components/admin/ProductToneSection";

// ---------- Theme Tab ----------
function ColorField({ label, value, onChange, placeholder = "", allowEmpty = false, testId }) {
    const effective = value || "#ffffff";
    return (
        <div>
            <label className="label-kicker mb-1 block">{label}</label>
            <div className="flex items-center gap-2">
                <input
                    type="color"
                    value={effective}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-10 h-9 border border-slate-300 cursor-pointer"
                    data-testid={testId ? `${testId}-picker` : undefined}
                />
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="input-field !py-1 !px-2 !text-xs flex-1 font-mono"
                    data-testid={testId}
                />
                {allowEmpty && value && (
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="text-xs text-slate-500"
                        title="Reset"
                    >
                        ↺
                    </button>
                )}
            </div>
        </div>
    );
}


export default function ThemeTab() {
    const { primaryHsl, updateTheme, presets } = useTheme();
    const { content, updateContent } = useSiteContent();
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const updateField = async (patch) => {
        setSaving(true);
        try {
            await updateContent({ ...content, ...patch });
            setSaved(true);
            setTimeout(() => setSaved(false), 1200);
        } finally {
            setSaving(false);
        }
    };

    const applyPreset = async (preset) => {
        setSaving(true);
        try {
            await updateTheme(preset.hsl);
            await updateContent({
                ...content,
                ...preset.content,
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 1200);
        } finally {
            setSaving(false);
        }
    };

    const sitePresets = [
        {
            id: "industrial",
            label: "Dark Industrial",
            accent: "0 80% 50%",
            hsl: "0 80% 50%",
            content: { font_family: "Inter", button_style: "sharp", titles_bold: true, accents_italic: false, hero_text_align: "left" },
        },
        {
            id: "minimal",
            label: "Minimal White",
            accent: "222 47% 11%",
            hsl: "222 47% 11%",
            content: { font_family: "Manrope", button_style: "rounded", titles_bold: false, accents_italic: true, hero_text_align: "center" },
        },
        {
            id: "racing",
            label: "Racing Red",
            accent: "354 72% 42%",
            hsl: "354 72% 42%",
            content: { font_family: "Oswald", button_style: "pill", titles_bold: true, accents_italic: false, hero_text_align: "left" },
        },
    ];

    return (
        <div data-testid="admin-theme-tab" className="space-y-8 max-w-4xl">
            {/* Theme presets */}
            <section>
                <div className="label-kicker text-slate-500 mb-2">Theme presets</div>
                <p className="text-sm text-slate-600 mb-4">
                    One-click combos (accent + font + button style + alignment). You can then tweak anything below.
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                    {sitePresets.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => applyPreset(p)}
                            className="flex flex-col p-4 border border-slate-200 hover:border-slate-400 transition-colors text-left"
                            data-testid={`theme-site-preset-${p.id}`}
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <span className="w-8 h-8" style={{ background: `hsl(${p.accent})` }} />
                                <span className="font-display font-black uppercase text-sm">{p.label}</span>
                            </div>
                            <div className="font-mono text-[10px] text-slate-500">
                                {p.content.font_family} · btn:{p.content.button_style} · {p.content.hero_text_align}
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Accent color */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Accent color</div>
                <p className="text-sm text-slate-600 mb-4">
                    Primary color for buttons, links and highlights.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {presets.map((p) => (
                        <button
                            key={p.hsl}
                            onClick={() => updateTheme(p.hsl)}
                            className={`flex items-center gap-3 px-3 py-3 border transition-colors ${primaryHsl === p.hsl ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}
                            data-testid={`theme-preset-${p.hsl.replace(/[^a-z0-9]/gi, "-")}`}
                        >
                            <span className="w-8 h-8 border border-slate-300" style={{ background: `hsl(${p.hsl})` }} />
                            <div className="text-left">
                                <div className="font-display font-bold uppercase text-sm">{p.label}</div>
                                <div className="font-mono text-[10px] text-slate-400">{p.hsl}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Typography */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Typography</div>
                <p className="text-sm text-slate-600 mb-4">
                    Font family + base size, applied site-wide. Loaded from Google Fonts.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="label-kicker mb-1 block">Font family</label>
                        <select
                            value={content.font_family}
                            onChange={(e) => updateField({ font_family: e.target.value })}
                            className="input-field"
                            data-testid="theme-font-family-input"
                        >
                            {FONT_OPTIONS.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                        <div className="mt-3 p-3 border border-slate-200 bg-slate-50 text-sm" style={{ fontFamily: `"${content.font_family}", system-ui, sans-serif` }}>
                            <div className="font-display font-black text-xl">Precision Turbocharger Parts.</div>
                            <div className="text-slate-600">The quick brown fox jumps over the lazy dog — 0123456789.</div>
                        </div>
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">Base font size: {content.font_size_base}px</label>
                        <input
                            type="range"
                            min="13"
                            max="20"
                            value={content.font_size_base}
                            onChange={(e) => updateField({ font_size_base: parseInt(e.target.value, 10) })}
                            className="w-full accent-[hsl(var(--primary))]"
                            data-testid="theme-font-size-input"
                        />
                        <div className="flex gap-3 mt-4">
                            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="theme-titles-bold-toggle">
                                <input
                                    type="checkbox"
                                    checked={content.titles_bold}
                                    onChange={(e) => updateField({ titles_bold: e.target.checked })}
                                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                                />
                                <span className="font-bold">Bold titles</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="theme-accents-italic-toggle">
                                <input
                                    type="checkbox"
                                    checked={content.accents_italic}
                                    onChange={(e) => updateField({ accents_italic: e.target.checked })}
                                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                                />
                                <span className="italic">Italic accents</span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>

            {/* Homepage layout */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Homepage layout</div>
                <p className="text-sm text-slate-600 mb-4">
                    Control the hero alignment and the overall button style.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="label-kicker mb-1 block">Hero text alignment</label>
                        <select
                            value={content.hero_text_align}
                            onChange={(e) => updateField({ hero_text_align: e.target.value })}
                            className="input-field"
                            data-testid="theme-hero-align-select"
                        >
                            <option value="left">Left (with right-side stats card)</option>
                            <option value="center">Center (no stats card)</option>
                            <option value="right">Right</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">Button style (site-wide)</label>
                        <select
                            value={content.button_style}
                            onChange={(e) => updateField({ button_style: e.target.value })}
                            className="input-field"
                            data-testid="theme-button-style-select"
                        >
                            <option value="sharp">Sharp (default, hard edges)</option>
                            <option value="rounded">Rounded (10px)</option>
                            <option value="pill">Pill (fully rounded)</option>
                        </select>
                    </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <ColorField
                        label="Hero kicker color"
                        value={content.hero_kicker_color}
                        onChange={(v) => updateField({ hero_kicker_color: v })}
                        placeholder="(theme default)"
                        allowEmpty
                        testId="theme-hero-kicker-color"
                    />
                    <ColorField
                        label="Hero title color"
                        value={content.hero_title_color}
                        onChange={(v) => updateField({ hero_title_color: v })}
                        placeholder="(slate-900)"
                        allowEmpty
                        testId="theme-hero-title-color"
                    />
                    <ColorField
                        label="Hero subtitle color"
                        value={content.hero_subtitle_color}
                        onChange={(v) => updateField({ hero_subtitle_color: v })}
                        placeholder="(slate-600)"
                        allowEmpty
                        testId="theme-hero-subtitle-color"
                    />
                </div>
            </section>

            {/* Logo sizes */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Logo sizes</div>
                <p className="text-sm text-slate-600 mb-4">
                    Adjust the logo dimensions across the site without replacing the image.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="label-kicker mb-1 block">Navbar logo: {content.navbar_logo_size || 36}px</label>
                        <input
                            type="range"
                            min="28"
                            max="120"
                            step="2"
                            value={content.navbar_logo_size || 36}
                            onChange={(e) => updateField({ navbar_logo_size: parseInt(e.target.value, 10) })}
                            className="w-full accent-[hsl(var(--primary))]"
                            data-testid="theme-navbar-logo-size-input"
                        />
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">Footer logo: {content.footer_logo_size || 36}px</label>
                        <input
                            type="range"
                            min="28"
                            max="120"
                            step="2"
                            value={content.footer_logo_size || 36}
                            onChange={(e) => updateField({ footer_logo_size: parseInt(e.target.value, 10) })}
                            className="w-full accent-[hsl(var(--primary))]"
                            data-testid="theme-footer-logo-size-input"
                        />
                    </div>
                </div>
            </section>

            {/* Branding */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Branding</div>
                <p className="text-sm text-slate-600 mb-4">
                    Replace the logo, add optional page background watermark, and customize the hero background image.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="label-kicker mb-1 block">Logo</label>
                        <ImageUploader
                            value={content.logo_url}
                            onChange={(url) => updateField({ logo_url: url || "" })}
                            testId="theme-logo-uploader"
                            recommended="240×80px · PNG transparent · ~50KB"
                        />
                        {content.logo_url && (
                            <button
                                type="button"
                                onClick={() => updateField({ logo_url: "" })}
                                className="mt-2 text-xs text-red-600 font-mono"
                                data-testid="theme-logo-clear"
                            >
                                Remove logo (revert to {content.brand_short})
                            </button>
                        )}
                        <div className="mt-5 pt-4 border-t border-slate-200" data-testid="theme-favicon-section">
                            <label className="label-kicker mb-1 block">Favicon (ikona në tab & Google)</label>
                            <p className="text-xs text-slate-500 mb-2">Bosh = përdoret logoja. Për logo të gjerë, ngarko një ikonë katrore (p.sh. vetëm simboli).</p>
                            <div className="flex items-start gap-4">
                                <div className="flex-1">
                                    <ImageUploader value={content.favicon_url} onChange={(url) => updateField({ favicon_url: url || "" })} testId="theme-favicon-uploader" recommended="512×512px · PNG katror" />
                                </div>
                                <div className="text-center" data-testid="theme-favicon-preview">
                                    <img src={`/api/favicon.png?size=64&v=${encodeURIComponent((content.favicon_url || content.logo_url || "").slice(-24))}`} alt="favicon" className="w-16 h-16 border border-slate-200 bg-white object-contain" />
                                    <div className="font-mono text-[10px] text-slate-500 mt-1">pamja</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">Page background image</label>
                        <ImageUploader
                            value={content.background_image_url}
                            onChange={(url) => updateField({ background_image_url: url || "" })}
                            testId="theme-bg-uploader"
                            recommended="1920×1080px · WEBP/JPG · ~250KB · Tileable or full-bleed"
                        />
                        {content.background_image_url && (
                            <>
                                <label className="label-kicker mt-3 mb-1 block">
                                    Page bg opacity: {Math.round((content.background_image_opacity ?? 0.06) * 100)}%
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="50"
                                    step="1"
                                    value={Math.round((content.background_image_opacity ?? 0.06) * 100)}
                                    onChange={(e) => updateField({ background_image_opacity: parseInt(e.target.value, 10) / 100 })}
                                    className="w-full accent-[hsl(var(--primary))]"
                                    data-testid="theme-bg-opacity-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => updateField({ background_image_url: "" })}
                                    className="mt-2 text-xs text-red-600 font-mono"
                                    data-testid="theme-bg-clear"
                                >
                                    Remove background
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <label className="label-kicker mb-1 block">Hero background image</label>
                    <p className="text-xs text-slate-500 mb-2">
                        The large image behind the main "Precision Turbocharger Parts." headline on the homepage.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6">
                    <ImageUploader
                        value={content.hero_background_url}
                        onChange={(url) => updateField({ hero_background_url: url || "" })}
                        testId="theme-hero-bg-uploader"
                        allowVideo
                        recommended="Foto 1920×800px (~300KB) OR video 1920×1080 MP4/WEBM (~5-15MB, muted, 5-15s loop)"
                    />
                        <div>
                            <label className="label-kicker mb-1 block">
                                Hero bg opacity: {Math.round((content.hero_background_opacity ?? 0.18) * 100)}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                step="1"
                                value={Math.round((content.hero_background_opacity ?? 0.18) * 100)}
                                onChange={(e) => updateField({ hero_background_opacity: parseInt(e.target.value, 10) / 100 })}
                                className="w-full accent-[hsl(var(--primary))]"
                                data-testid="theme-hero-bg-opacity-input"
                            />
                            {content.hero_background_url && (
                                <button
                                    type="button"
                                    onClick={() => updateField({ hero_background_url: "" })}
                                    className="mt-2 text-xs text-red-600 font-mono"
                                    data-testid="theme-hero-bg-clear"
                                >
                                    Remove hero background
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <label className="label-kicker mb-1 block">Hero side media (foto / video)</label>
                    <p className="text-xs text-slate-500 mb-2">
                        Mbulon të gjithë zonën e Hero-s (pas tekstit), në të gjitha pajisjet. Ruhet në storage të përhershëm.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <ImageUploader
                            value={content.hero_media_url}
                            onChange={(url) => updateField({ hero_media_url: url || "" })}
                            testId="theme-hero-media-uploader"
                            allowVideo
                            recommended="Foto 1200×900px (~300KB) OSE video MP4/WEBM (~5-15MB, 5-15s loop)"
                        />
                        {content.hero_media_url && (
                            <div>
                                <button
                                    type="button"
                                    onClick={() => updateField({ hero_media_url: "" })}
                                    className="text-xs text-red-600 font-mono"
                                    data-testid="theme-hero-media-clear"
                                >
                                    Remove hero side media
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <ProductToneSection content={content} updateField={updateField} />

            <section className="border-t border-slate-200 pt-6" data-testid="theme-invoice-moved-note">
                <div className="label-kicker text-slate-500 mb-1">Invoice / Preventiv template</div>
                <p className="text-sm text-slate-600">Cilësimet e faturës janë zhvendosur te tab-i <b>Invoice</b>.</p>
            </section>

            {/* Login page customization */}
            <section className="border-t border-slate-200 pt-6">
                <div className="label-kicker text-slate-500 mb-2">Login page</div>
                <p className="text-sm text-slate-600 mb-4">
                    Full control over the sign-in screen: layout, background image, logo size, form style and all text colors.
                </p>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="label-kicker mb-1 block">Layout</label>
                        <select
                            value={content.login_layout}
                            onChange={(e) => updateField({ login_layout: e.target.value })}
                            className="input-field"
                            data-testid="theme-login-layout-select"
                        >
                            <option value="centered">Centered (form in middle, full-bleed background)</option>
                            <option value="split">Split (image left · form right)</option>
                        </select>
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">Form style</label>
                        <select
                            value={content.login_form_style}
                            onChange={(e) => updateField({ login_form_style: e.target.value })}
                            className="input-field"
                            data-testid="theme-login-form-style-select"
                        >
                            <option value="card">Card (white w/ shadow)</option>
                            <option value="glass">Glass (backdrop-blur)</option>
                            <option value="minimal">Minimal (no card)</option>
                        </select>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-4">
                    <div>
                        <label className="label-kicker mb-1 block">Login background image</label>
                        <ImageUploader
                            value={content.login_background_url}
                            onChange={(url) => updateField({ login_background_url: url || "" })}
                            testId="theme-login-bg-uploader"
                            recommended="1920×1200px · WEBP/JPG · ~300KB · works on dark overlay"
                        />
                        {content.login_background_url && (
                            <button
                                type="button"
                                onClick={() => updateField({ login_background_url: "" })}
                                className="mt-2 text-xs text-red-600 font-mono"
                                data-testid="theme-login-bg-clear"
                            >
                                Remove (revert to default compressor image)
                            </button>
                        )}
                    </div>
                    <div>
                        <label className="label-kicker mb-1 block">
                            Background opacity: {Math.round((content.login_background_opacity ?? 0.4) * 100)}%
                        </label>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            step="1"
                            value={Math.round((content.login_background_opacity ?? 0.4) * 100)}
                            onChange={(e) => updateField({ login_background_opacity: parseInt(e.target.value, 10) / 100 })}
                            className="w-full accent-[hsl(var(--primary))]"
                            data-testid="theme-login-bg-opacity-input"
                        />
                        <label className="flex items-center gap-2 text-sm cursor-pointer mt-3" data-testid="theme-login-bg-blur-toggle">
                            <input
                                type="checkbox"
                                checked={content.login_background_blur}
                                onChange={(e) => updateField({ login_background_blur: e.target.checked })}
                                className="w-4 h-4 accent-[hsl(var(--primary))]"
                            />
                            <span>Blur background</span>
                        </label>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="label-kicker mb-1 block">
                            Logo size: {content.login_logo_size || 36}px
                        </label>
                        <input
                            type="range"
                            min="32"
                            max="160"
                            step="2"
                            value={content.login_logo_size || 36}
                            onChange={(e) => updateField({ login_logo_size: parseInt(e.target.value, 10) })}
                            className="w-full accent-[hsl(var(--primary))]"
                            data-testid="theme-login-logo-size-input"
                        />
                    </div>
                </div>

                <div className="label-kicker text-slate-500 mb-2 mt-4">Text colors</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <ColorField
                        label="Slogan (line 1 + 3)"
                        value={content.login_hero_line1_color || "#ffffff"}
                        onChange={(v) => updateField({ login_hero_line1_color: v })}
                        testId="theme-login-line1-color"
                    />
                    <ColorField
                        label="Accent line (line 2)"
                        value={content.login_hero_line2_color || ""}
                        onChange={(v) => updateField({ login_hero_line2_color: v })}
                        placeholder="(accent)"
                        allowEmpty
                        testId="theme-login-line2-color"
                    />
                    <ColorField
                        label="Subtitle"
                        value={content.login_hero_subtitle_color || "#94a3b8"}
                        onChange={(v) => updateField({ login_hero_subtitle_color: v })}
                        testId="theme-login-subtitle-color"
                    />
                    <ColorField
                        label="Form text"
                        value={content.login_form_text_color || "#0f172a"}
                        onChange={(v) => updateField({ login_form_text_color: v })}
                        testId="theme-login-form-color"
                    />
                </div>

                <div className="mt-4 text-xs">
                    <a
                        href="/login"
                        target="_blank"
                        rel="noreferrer"
                        className="text-[hsl(var(--primary))] underline font-mono"
                        data-testid="theme-login-preview-link"
                    >
                        Open /login in new tab to preview ↗
                    </a>
                </div>
            </section>

            <div className="h-6 font-mono text-xs text-slate-500">
                {saving ? "Saving…" : saved ? "✓ Saved" : ""}
            </div>
        </div>
    );
}

